import { Col, Form, Row, Select } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import DefaultButton from 'components/atoms/DefaultButton';
import DefaultCard from 'components/atoms/DefaultCard';
import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import { editSiteFarmMappings } from 'store/modules/agriWebb/actions';
import AgriWebbActionType from 'store/modules/agriWebb/constants';
import { AgriWebbFarm } from 'store/modules/agriWebb/types';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import { selectSitesAsArray } from 'store/modules/sites/selectors';
import { MAX_WIDTH_MOBILE } from 'style/constants';
import { AgriWebbSiteIntegrations } from 'types/models/agriwebb';
import { SiteId } from 'types/models/site';

import './styles.less';

interface Props {
  agriWebbFarms: AgriWebbFarm[];
  enterpriseId: number;
  existingMappings: AgriWebbSiteIntegrations[];
  onSuccess: () => void;
  readOnly: boolean;
}

const actions = [AgriWebbActionType.EDIT_SITE_FARM_MAPPINGS_REQUEST];
const selectIsSubmitting = makeSelectIsLoading(actions);

export default function AgriWebbSiteFarmMappingForm({
  agriWebbFarms,
  enterpriseId,
  existingMappings,
  onSuccess,
  readOnly
}: Props): JSX.Element {
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSubmitting = useSelector(selectIsSubmitting);
  const sites = useSelector(selectSitesAsArray);
  const [isInteractionDisabled, setIsInteractionDisabled] = useState(false);

  const farmOptions = useMemo(() => {
    return agriWebbFarms.map(({ id, name }) => ({
      label: name,
      value: id,
      key: id.toString()
    }));
  }, [agriWebbFarms]);

  const initialValues = useMemo(() => {
    const existingMappingsMap: Record<SiteId, string> = existingMappings.reduce(
      (acc, mapping) => ({
        ...acc,
        [mapping.siteId]: mapping.agriWebbFarmId
      }),
      {}
    );

    return sites.reduce(
      (acc, site) => ({
        ...acc,
        [site.id]: existingMappingsMap[site.id] || undefined
      }),
      {} as Record<SiteId, string | undefined>
    );
  }, [sites, existingMappings]);

  const submit = useCallback(() => {
    form.validateFields().then((values) => {
      const mapping = Object.keys(values).map((key) => {
        return { siteId: Number(key), agriWebbFarmId: values[key] as string };
      });
      dispatch(
        editSiteFarmMappings(
          { siteMapping: mapping, enterpriseId },
          () => {
            onSuccess();
            setIsInteractionDisabled(true);
          }
        )
      );
    });
  }, [form, dispatch, enterpriseId, onSuccess]);

  const back = () => navigate(-1);

  return (
    <>
      {farmOptions.length && initialValues ? (
        <DefaultForm
          form={form}
          layout="vertical"
          name="SitesMapping"
          initialValues={initialValues}
        >
          {!isMobile && (
            <DefaultCard bodyStyle={{ padding: 0 }} className="legacy-not-last-child">
              <div className="AgriWebbSiteFarmMappingForm-form-header">
                <Row align="middle">
                  <Col span={12}>
                    <div className="body-emphasis-copy">Site</div>
                  </Col>
                  <Col span={12}>
                    <div className="body-emphasis-copy">AgriWebb Farm</div>
                  </Col>
                </Row>
              </div>
              {sites.map((site) => {
                return (
                  <Row
                    key={site.id}
                    align="middle"
                    className="AgriWebbSiteFarmMappingForm-row"
                  >
                    <Col span={12}>
                      <div className="body-copy">{site.name}</div>
                    </Col>
                    <Col span={12}>
                      <DefaultFormItem
                        className="AgriWebbSiteFarmMappingForm-selector"
                        name={site.id}
                        readOnly={readOnly}
                        formatReadOnlyValue={(value) => farmOptions.find((option) => option.value === value)?.label
                          || value
                          || 'Not mapped yet'}
                        required
                      >
                        <Select
                          className="AgriWebbSiteFarmMappingForm-picker"
                          options={farmOptions}
                          placeholder="Select a farm"
                        />
                      </DefaultFormItem>
                    </Col>
                  </Row>
                );
              })}
            </DefaultCard>
          )}
          {isMobile && (
            <DefaultCard bodyStyle={{ padding: 0 }} className="legacy-not-last-child">
              {sites.map((site) => {
                return (
                  <div
                    key={site.id}
                    className="AgriWebbSiteFarmMappingForm-row vertical-gap-8"
                  >
                    <div className="body-copy">
                      <span className="body-emphasis-copy">From site: </span>
                      {site.name}
                    </div>
                    <DefaultFormItem
                      label="To AgriWebb Farm:"
                      className="AgriWebbSiteFarmMappingForm-selector"
                      name={site.id}
                      readOnly={readOnly}
                      formatReadOnlyValue={(value) => farmOptions.find((option) => option.value === value)?.label
                        || value
                        || 'Not mapped yet'}
                      required
                    >
                      <Select
                        className="AgriWebbSiteFarmMappingForm-picker"
                        options={farmOptions}
                        placeholder="Select a farm"
                      />
                    </DefaultFormItem>
                  </div>
                );
              })}
            </DefaultCard>
          )}
          <div className="AgriWebbSiteFarmMappingForm-footer">
            <DefaultButton
              className={classNames(
                'AgriWebbSiteFarmMappingForm-footer-back-button',
                {
                  disabled: isInteractionDisabled
                }
              )}
              onClick={back}
              disabled={isInteractionDisabled}
            >
              Back
            </DefaultButton>

            {!readOnly && (
              <DefaultButton
                className={classNames(
                  'AgriWebbSiteFarmMappingForm-footer-submit-button',
                  {
                    disabled: isInteractionDisabled
                  }
                )}
                onClick={submit}
                loading={isSubmitting}
                disabled={isInteractionDisabled}
              >
                Submit
              </DefaultButton>
            )}
          </div>
        </DefaultForm>
      ) : (
        <>
          <br />
          <DefaultCard>
            <div className="body-copy">No Sites or Farms to map</div>
          </DefaultCard>
          <div className="AgriWebbSiteFarmMappingForm-footer">
            <DefaultButton
              className="AgriWebbSiteFarmMappingForm-footer-cancel-button"
              onClick={back}
            >
              Back
            </DefaultButton>
          </div>
        </>
      )}
    </>
  );
}
